import * as ko from "knockout";
import * as L from "leaflet";
import "leaflet.markercluster/dist/leaflet.markercluster.js";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.default.css";
import "leaflet/dist/leaflet.css";
import * as moment from "moment";
import * as AppointmentData from "../data/appointmentData";
import { IApplication } from "../interfaces/application";
import * as Data from "../interfaces/data";
import * as Constants from "./constants";
import * as Global from "./global";
import { getMapTileLayer } from "./mapUtil";
import * as UI from "./ui";

interface ILocationLogViewModel {
    LocationLogs: KnockoutObservableArray<AppointmentData.IAppointmentLocationLog>;
    DataLoaded: KnockoutObservable<boolean>;
}

// data needed to load this control with form data
export interface ILoadParams {
    LocationLogs: AppointmentData.IAppointmentLocationLog[];
}

// a control for displaying a map that contains the
// appointment location log entires
export class AppointmentLocationLogMapControl {

    private container: JQuery;
    private map: L.Map | null = null;
    private viewModel: ILocationLogViewModel;

    public render(container: JQuery): JQueryPromise<any> {
        const me: AppointmentLocationLogMapControl = this;
        const app: IApplication = Global.app();
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.container = container;
        this.viewModel = {
            LocationLogs: ko.observableArray<AppointmentData.IAppointmentLocationLog>(),
            DataLoaded: ko.observable<boolean>(false),
        };

        container.hide();

        app.templates().render("appointmentlocationlogmap", "template_appointmentlocationlogmap", this.container)
            .done(() => {
                ko.applyBindings(me.viewModel, me.container[0]);
                deferred.resolve();
            });

        return deferred.promise();
    }

    public load(appointmentID: number): JQueryPromise<any> {

        const me: AppointmentLocationLogMapControl = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        AppointmentData.GetLocationLogs(appointmentID)
            .done((response: Data.IResponse<AppointmentData.IAppointmentLocationLog[]>) => {

                if (response.Success) {
                    // filter out logs where the lat/long is 0
                    const validLogs = response.Payload.filter((item) => {
                        return item.Latitude !== 0 && item.Longitude !== 0;
                    });

                    me.viewModel.LocationLogs(validLogs);
                    me.viewModel.DataLoaded(true);
                    me.loadMap(response.Payload);
                    deferred.resolve();
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                    deferred.reject();
                }
            })
            .fail(() => {
                deferred.reject();
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });

        return deferred.promise();
    }

    public show(): void {
        this.container.show();
    }

    public hide(): void {
        this.container.hide();
    }

    private loadMap(locationLogs: AppointmentData.IAppointmentLocationLog[]): void {

        if (locationLogs.length === 0) {
            return;
        }

        try {
            this.initMap();
            this.drawMapMarkers(locationLogs);
        } catch (ex) {
            UI.showErrorMessage([Constants.PHRASE_PAGELOADERROR]);
        }
    }

    private initMap(): void {
        if (this.map !== null) {
            this.map.remove();
        }

        const mapContainer: JQuery<HTMLElement> = this.container.find(".map");
        const tiles: L.TileLayer = getMapTileLayer();
        this.map = L.map(mapContainer[0], { layers: [tiles] });
    }

    private drawMapMarkers(locationLogs: AppointmentData.IAppointmentLocationLog[]): void {
        const markers: L.MarkerClusterGroup = L.markerClusterGroup({
            chunkedLoading: true,
            spiderfyDistanceMultiplier: 1.2,
        });

        locationLogs.forEach((point, index) => {
            if (point.Latitude === 0 && point.Longitude === 0) { return; }
            const iconHtml: string = "<i class=\"fa fa-map-marker fa-3x\"></i>";
            const marker: L.Marker = L.marker(L.latLng(point.Latitude, point.Longitude),
                {
                    title: point.Description,
                    icon: new L.DivIcon({ html: iconHtml, className: "map-icon", iconSize: [30, 35] }),
                });

            marker.bindPopup(`${point.Description}<br /> ${moment(point.RecordedDateTime).format(Constants.MOMENT_FORMAT_DATETIME)}`);
            markers.addLayer(marker);
        });

        if (this.map !== null) {
            const bounds = markers.getBounds();
            if (bounds.isValid()) {
                this.map.addLayer(markers);
                this.map.fitBounds(bounds);
            }
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/common/appointmentLocationLogMap.ts